import { useMutation } from '@apollo/client'
import commerceConfig from '@commerce/config'
import usePrice, { formatPrice } from '@commerce/product/use-price'
import type { Product, ProductVariant } from '@commerce/types/product'
import cartLinesAddMutation, {
  cartAddMultipleLinesVars,
} from '@commerce/utils/mutations/cart-lines-add'
import { SelectedOptions } from '@components/product/helpers'
import ColourCardOption from '@components/product/ProductOptions/ProductOptionByLitre'
import { Button, useUI } from '@components/ui'
import { pinterestAddToCartEvent } from '@lib/events/addToCart'
import {
  PRODUCT_CUSTOM_ATTRIBUTES_KEYS,
  findMetafieldValueByKey,
} from '@lib/helpers/general'
import processLocale from '@lib/locale'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
interface props {
  options: ProductVariant[]
  currencyCode: string
  selectedSibling: Product
  parentProduct?: Product
}
const ColourCardProcess: FC<props> = ({
  options,
  currencyCode,
  selectedSibling,
  parentProduct,
}) => {
  const {
    PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY,
    RETAIL_PRICE_CUSTOM_ATTRIBUTE_KEY,
  } = PRODUCT_CUSTOM_ATTRIBUTES_KEYS
  const { locale } = useRouter()
  const { openCartNotification } = useUI()
  const {
    config: { context },
  } = commerceConfig
  const currentLocale = processLocale(locale)
  const currentContext = context?.[currentLocale]
  const taxAmount = currentContext?.taxAmount
  const { t, i18n } = useTranslation('product')
  const { t: tcommon } = useTranslation('common')
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const [selectedOption, setSelectedOption] = useState<ProductVariant | null>(
    null
  )
  const [loading, setLoading] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)
  const [quantity, setQuantity] = useState(0)
  useEffect(() => {
    let subTotals = 0
    Object.keys(selectedOptions).forEach((key) => {
      const variant = options.find((o) => o.id === key)
      const quantity = Number(selectedOptions[key])
      setQuantity(quantity)
      if (variant && variant.id) {
        if (variant && variant.price) {
          subTotals = subTotals + quantity * Number(variant.price)
        }
      }
    })
    setTotalPrice(subTotals)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions])
  const { price: beforeTaxTotal } = usePrice({
    amount: totalPrice!,
    currencyCode: currencyCode!,
    suffix: `(${tcommon('excl-vat')})`,
  })

  const { price: afterTaxTotal } = usePrice({
    amount: totalPrice ? totalPrice * (1 + taxAmount || 1) : 0,
    currencyCode: currencyCode!,
    suffix: `(${tcommon('incl-vat-approx')})`,
  })

  // const { hits, sendEvent } = useHits()
  const [addItem] = useMutation(cartLinesAddMutation, {
    onCompleted() {
      pinterestAddToCartEvent(
        Object.keys(selectedOptions).map((key) => {
          return {
            product_id: selectedSibling?.id || '',
            product_variant:
              selectedOptions?.size ||
              selectedSibling?.variants?.[0]?.name ||
              '',
            product_variant_id:
              selectedSibling?.variants?.find(
                (variant) =>
                  variant?.name?.toLowerCase() ===
                  selectedOptions?.size?.toString().toLowerCase()
              )?.id ||
              selectedSibling?.variants?.[0]?.id ||
              '',
            product_price:
              selectedSibling?.variants?.find(
                (variant) =>
                  variant?.name?.toLowerCase() ===
                  selectedOptions?.size?.toString().toLowerCase()
              )?.price ||
              selectedSibling?.variants?.[0]?.price ||
              0,
            product_quantity: Number(selectedOptions[key]),
            currency: selectedSibling?.price?.currencyCode || 'AUD',
          }
        })
      )
      openCartNotification(
        `${quantity} ${selectedSibling?.name} ${tcommon('added-to-cart')}`
      )
      setLoading(false)
    },
    onError() {
      setLoading(false)
    },
  })

  const addToCart = async () => {
    setLoading(true)
    addItem({
      variables: cartAddMultipleLinesVars(
        Object.keys(selectedOptions).map((key) => {
          const variant = options.find((o) => o.id === key)
          const recipe = findMetafieldValueByKey(variant?.metafields!, 'recipe')
          const customAttributes = [
            {
              key: PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY,
              value: parentProduct?.handle || '',
            },
          ]
          // if (recipe) {
          //   customAttributes.push({
          //     key: '_recipe',
          //     value: recipe,
          //   })
          // }
          if (variant?.originalPrice && variant.originalPrice > variant.price) {
            customAttributes.push({
              key: RETAIL_PRICE_CUSTOM_ATTRIBUTE_KEY,
              value: formatPrice({
                amount: variant.originalPrice,
                currencyCode: currencyCode!,
                locale,
              }),
            })
          }
          return {
            merchandiseId: key,
            quantity: Number(selectedOptions[key]),
            attributes: customAttributes,
          }
        }),
        locale
      ),
      context: { locale },
    })
  }
  return (
    <div className="pt-8">
      <div className="flex h-full flex-col justify-between ">
        <div>
          <div className="flex items-center justify-between">
            <div className="body-1-bold text-ui-dark-grey">
              {tcommon('please-select-size')}:
            </div>
          </div>
          <div className="mt-4">
            <ColourCardOption
              options={options}
              currencyCode={currencyCode}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              setSelectedOptions={setSelectedOptions}
            />
          </div>
        </div>

        <div className="my-8 h-px w-full bg-ui-grey-25" />
        <div className="flex flex-col">
          <div className="label flex flex-col gap-y-3 pb-8">
            <div className="flex justify-between">
              <div className="font-bold uppercase">
                {tcommon('size-selected')}
              </div>
              {selectedOption?.name ? (
                <div>{selectedOption?.name}</div>
              ) : (
                <div>-</div>
              )}
            </div>
            {totalPrice !== 0 ? (
              <div className="flex justify-between">
                <div className="font-bold uppercase">{tcommon('total')}</div>
                <div className="flex flex-col text-right">
                  {locale?.endsWith('eu') ? afterTaxTotal : ''}
                  <span>{`${beforeTaxTotal} ${
                    locale?.endsWith('gb') ? `(incl. GST / VAT)` : ''
                  }`}</span>
                </div>
              </div>
            ) : null}
          </div>
          {i18n.exists('incl-vat-approx-detail') && (
            <div className="body-2 pb-4">{`${tcommon(
              'incl-vat-approx-detail'
            )}`}</div>
          )}
          <Button
            aria-label={`${t('add-to-cart')}`}
            type="button"
            width="100%"
            onClick={addToCart}
            loading={loading}
            disabled={Object.keys(selectedOptions).length === 0 || loading}
          >
            {totalPrice == 0
              ? `${tcommon('make-your-selection')}`
              : `${tcommon('add-to-cart')}`}
          </Button>
        </div>
      </div>
    </div>
  )
}
export default ColourCardProcess
